export const CHARTS = [
  { id: 'host-cpu', title: 'Host CPU' },
  { id: 'host-memory', title: 'Host memory' },
  { id: 'host-disk-io', title: 'Host disk I/O' },
  { id: 'container-cpu', title: 'Container CPU' },
  { id: 'container-memory', title: 'Container memory' },
  { id: 'container-io', title: 'Container network I/O' },
] as const;

export const GAME_SERVER_HEARTBEAT_CHART_ID = 'game-server-heartbeats';

export type ChartID = typeof CHARTS[number]['id'] | typeof GAME_SERVER_HEARTBEAT_CHART_ID;
export interface Chart {
  chart_id: string;
  from: string;
  to: string;
  interval: string;
  unit: 'cores' | 'bytes' | 'bytes/sec' | 'percent' | 'count' | 'seconds';
  series: { label: string; points: { time: string; value: number | null }[] }[];
  unavailable_metrics?: string[];
}

export interface GameServerSummary {
  environment: string;
  observed_at: string | null;
  running: number | null;
  reporting: number;
  tables: number | null;
  players: number | null;
  mean_age_seconds: number | null;
  mean_lifetime_seconds: number | null;
  completed_containers: number;
  partial: boolean;
}
export interface GameServerMetrics {
  from: string;
  to: string;
  interval: string;
  freshness_seconds: number;
  environments: string[];
  summaries: GameServerSummary[];
  charts: Chart[];
}
export interface TimeWindows {
  default: string;
  windows: { id: string; label: string; duration_seconds: number }[];
}
export type TimeFilter = ({ window: string } | { from: string; to: string }) & { interval?: string };

export interface UserMonitor {
  online_users: number;
  playing_users: number;
  observed_at: string;
}

export type ContainerStatus = 'healthy' | 'unhealthy' | 'running' | 'starting' | 'created' | 'paused' | 'restarting' | 'removing' | 'exited' | 'dead' | 'unknown' | 'stale' | 'missing';
export interface ContainerHealthItem {
  host_name: string;
  container_id: string;
  container_name?: string;
  service_name?: string;
  last_seen: string;
  age_seconds: number;
  status: ContainerStatus;
  telemetry_status: 'healthy' | 'stale' | 'missing';
  state: 'created' | 'running' | 'paused' | 'restarting' | 'removing' | 'exited' | 'dead' | null;
  health_status: 'starting' | 'healthy' | 'unhealthy' | null;
  state_last_seen: string | null;
  health_last_seen: string | null;
  restarts: number | null;
  uptime_seconds: number | null;
  uptime_last_seen: string | null;
}
export interface ContainerHealth {
  observed_at: string;
  lookback_seconds: number;
  healthy_seconds: number;
  stale_seconds: number;
  items: ContainerHealthItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface BackendErrorEvent {
  time: string;
  service: string;
  level: string;
  message: string;
  status: number | null;
  latency_ms: number | null;
  error: string;
  request_id: string;
  source: string;
  stack: string;
  panic_stack: string;
  details?: Record<string, unknown>;
}
export interface BackendErrors {
  from: string;
  to: string;
  interval: string;
  service: string;
  error_logs: number;
  failed_requests: number;
  fatal_logs: number;
  p95_latency_ms: number | null;
  trend: { time: string; level: string; error_logs: number; failed_requests: number }[];
  top_routes: { message: string; status: number; requests: number; average_latency_ms: number; last_seen: string }[];
  top_messages: { message: string; level: string; events: number; last_seen: string }[];
  recent_errors: BackendErrorEvent[];
  recent_requests: BackendErrorEvent[];
}

export type BackendLogKind = 'requests' | 'errors';
export interface BackendLogsPage {
  from: string;
  to: string;
  service: string;
  limit: number;
  items: BackendErrorEvent[];
  has_more: boolean;
  next_cursor: string;
}
